// FT3D COMBAT DISTANCE — the browser golden source for range/reach geometry.
//
// Range extends to the target's footprint rim: touching the fixed range ring
// is sufficient. The caster keeps its excess-over-5-foot size credit. Rendered
// mesh bounds, standee aspect, cosmetic scale and cover volumes are not inputs.
//
// Frontend range authority. The legacy PHP distance helper is a separate path;
// it is not a validation gate for browser-owned range eligibility.
// Regression vectors: tools/test/ft3d-combat-distance-frontend-vectors.json.

export const FT3D_COMBAT_DISTANCE_CONTRACT = 'ft3d-combat-distance-v3';
export const FT3D_SELF_AOE_SIZE_CONTRACT = 'ft3d-self-aoe-size-v1';

export function combatSizeCreditFt(sizeFeet) {
  const size = Number(sizeFeet);
  return Number.isFinite(size) ? Math.max(0, (size - 5) / 2) : 0;
}

export function combatTargetRadiusFt(sizeFeet) {
  const size = Number(sizeFeet);
  return Number.isFinite(size) ? Math.max(0, size) / 2 : 0;
}

/**
 * v2 (owner 2026-09-01): vertical separation is the gap between the two occupied SPANS (the
 * size-tier cube around each persisted centre), 0 while the bodies overlap vertically. A 0.33 ft
 * terrain difference no longer turns adjacent creatures into "out of reach by 0.0 ft". A sizeless
 * target is a point. Mirrors ft_combat_vertical_separation_ft().
 */
export function combatVerticalSeparationFt(dz, sourceSizeFeet, targetSizeFeet) {
  const half = size => {
    const value = Number(size);
    return Number.isFinite(value) ? Math.max(0, value) / 2 : 0;
  };
  return Math.max(0, Math.abs(Number(dz) || 0) - half(sourceSizeFeet) - half(targetSizeFeet));
}

export function combatCenterDistanceFt(source, target, options = {}) {
  const dx = Number(target?.x ?? 0) - Number(source?.x ?? 0);
  const dz = combatVerticalSeparationFt(
    Number(target?.z ?? 0) - Number(source?.z ?? 0),
    options.sourceSizeFeet,
    options.targetSizeFeet,
  );
  const dy = options.depthAuthored === false
    ? 0
    : Number(target?.y ?? 0) - Number(source?.y ?? 0);
  // Take the target rim on the floor plane before combining the vertical span
  // gap. Subtracting a radius from the final 3D distance would count it again
  // for a target directly overhead and incorrectly extend vertical reach.
  const planarGap = Math.max(0, Math.sqrt(dx * dx + dy * dy) - combatTargetRadiusFt(options.targetSizeFeet));
  const centerDistance = Math.sqrt(planarGap * planarGap + dz * dz);
  return Math.max(
    0,
    centerDistance
      - combatSizeCreditFt(options.sourceSizeFeet),
  );
}

export const FT3D_COMBAT_SEGMENT_CONTRACT = 'ft3d-combat-segment-v1';

/**
 * WYSIWYG reach to a WALL RUN (owner 2026-09-10: "from mesh to mesh is the closest point to point …
 * really from our rim, similar to AOE logic"). The target is the run's own segment — the plane
 * `axis = at` over `span`, standing `z.min..z.max` — so a 115 ft wall is in reach anywhere along its
 * length, not only near its midpoint. Measured from the source's centre to the NEAREST point of the
 * segment (planar, plus the vertical gap between the source's size-tier span and the wall's height),
 * minus the source's own rim credit. A segment has no size credit of its own: it IS the geometry.
 * Mirrors ft_combat_segment_distance_ft().
 */
export function combatSegmentDistanceFt(source, segment, options = {}) {
  const sx = Number(source?.x ?? 0);
  const sy = Number(source?.y ?? 0);
  const sz = Number(source?.z ?? 0);
  const at = Number(segment?.at ?? 0);
  const spanA = Number(segment?.span?.min ?? 0);
  const spanB = Number(segment?.span?.max ?? 0);
  const along = segment?.axis === 'x' ? sy : sx;
  const across = segment?.axis === 'x' ? sx : sy;
  const beyond = Math.max(0, Math.min(spanA, spanB) - along, along - Math.max(spanA, spanB));
  const planar = Math.hypot(across - at, beyond);
  const size = Number(options.sourceSizeFeet);
  const half = Number.isFinite(size) ? Math.max(0, size) / 2 : 0;
  const zA = Number(segment?.z?.min ?? 0);
  const zB = Number(segment?.z?.max ?? 0);
  const vertical = Math.max(0, Math.min(zA, zB) - (sz + half), (sz - half) - Math.max(zA, zB));
  return Math.max(0, Math.hypot(planar, vertical) - combatSizeCreditFt(options.sourceSizeFeet));
}

export function combatCenterLimitFt(rangeFeet, sourceSizeFeet, targetSizeFeet) {
  const range = Number(rangeFeet);
  return Math.max(0, Number.isFinite(range) ? range : 0)
    + combatSizeCreditFt(sourceSizeFeet)
    + combatTargetRadiusFt(targetSizeFeet);
}

/**
 * Runtime dimension for a CENTERED Self AOE. The catalog keeps the authored
 * distance; larger gameplay spaces add only their excess beyond the ordinary
 * 5-foot combat cell. Radial shapes store a radius, while square/cube store a
 * full side length, so the latter add the credit on both sides.
 *
 * Directional Self shapes keep their authored length: their source/rim
 * semantics are a separate contract from a centered emanation.
 */
export function selfAoeEffectiveDistanceFt(distanceFeet, sourceSizeFeet, shape) {
  const distance = Number(distanceFeet);
  const base = Math.max(0, Number.isFinite(distance) ? distance : 0);
  const normalizedShape = String(shape ?? '').toLowerCase();
  if (!['sphere', 'circle', 'cylinder', 'cube', 'square'].includes(normalizedShape)) return base;
  const credit = combatSizeCreditFt(sourceSizeFeet);
  return base + (normalizedShape === 'cube' || normalizedShape === 'square' ? credit * 2 : credit);
}
